import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isBefore, parseISO } from 'date-fns';
import { editOffer, viewOffer, listAvailableSubscriptions } from '../api';
import { OFFER_ERROR_MESSAGES } from '../errors';
import {
  OfferFormData,
  OfferFormErrors,
  INITIAL_FORM_DATA,
} from '../types/offerFormTypes';
import { SubscriptionPlanSummary } from '../api/viewOfferApiService';
import type { SubscriptionPlan } from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';

// ============================================================================
// Hook
// ============================================================================

export function useEditOffer(offerId: string) {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<OfferFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<OfferFormErrors>({});
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState<string>('active');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // External data
  const [availablePlans, setAvailablePlans] = useState<
    { id: string; label: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  // Snapshot of the offer at load time, used to detect "no edits" on cancel.
  const originalRef = useRef<{
    formData: OfferFormData;
    selectedPlans: string[];
    startDateMs: number | null;
    endDateMs: number | null;
  } | null>(null);

  // Fetch offer data and available subscription plans
  useEffect(() => {
    const fetchData = async () => {
      if (!offerId) {
        return;
      }
      setIsLoading(true);
      try {
        const [offerResult, plansResult] = await Promise.all([
          viewOffer({ offerId }),
          listAvailableSubscriptions(),
        ]);

        if (offerResult.success && offerResult.data) {
          const offer = offerResult.data;
          const initialFormData: OfferFormData = {
            offerName: offer.offerName,
            offerType:
              offer.discountType === 'PERCENTAGE' ? 'Procentowy' : 'Kwotowy',
            discountValue: offer.discountValue.toString(),
            discountId: offer.discountId || '',
          };
          const initialPlans = offer.subscriptionPlans.map(
            (p: SubscriptionPlanSummary) => p.id
          );
          const initialStart = offer.validityPeriod.startDate
            ? parseISO(offer.validityPeriod.startDate)
            : undefined;
          const initialEnd = offer.validityPeriod.endDate
            ? parseISO(offer.validityPeriod.endDate)
            : undefined;

          setFormData(initialFormData);
          setSelectedPlans(initialPlans);
          setStartDate(initialStart);
          setEndDate(initialEnd);
          setStatus(offer.status);

          originalRef.current = {
            formData: initialFormData,
            selectedPlans: initialPlans,
            startDateMs: initialStart ? initialStart.getTime() : null,
            endDateMs: initialEnd ? initialEnd.getTime() : null,
          };

          // Combine current offer's plans with available plans
          const currentPlans = offer.subscriptionPlans.map(
            (p: SubscriptionPlanSummary) => ({
              id: p.id,
              label: p.name,
            })
          );

          const available = (plansResult.data || []).map(
            (plan: SubscriptionPlan) => ({
              id: plan.id,
              label: plan.planName,
            })
          );

          // Unique plans by ID
          const allPlans = [...currentPlans];
          available.forEach((p) => {
            if (!allPlans.find((ap) => ap.id === p.id)) {
              allPlans.push(p);
            }
          });

          setAvailablePlans(allPlans);
        } else {
          setSnackbarError(
            offerResult.error || OFFER_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setSnackbarError(OFFER_ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [offerId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Basic validation
      const newErrors: OfferFormErrors = {};

      if (!formData.offerName.trim()) {
        newErrors.offerName = OFFER_ERROR_MESSAGES.OFFER_NAME_REQUIRED;
      } else if (formData.offerName.length > 200) {
        newErrors.offerName = OFFER_ERROR_MESSAGES.OFFER_NAME_MAX_LENGTH;
      }

      const discValueStr = formData.discountValue.replace(',', '.').trim();
      if (!discValueStr) {
        newErrors.discountValue = OFFER_ERROR_MESSAGES.OFFER_NAME_REQUIRED;
      } else {
        const discValue = parseFloat(discValueStr);
        if (Number.isNaN(discValue)) {
          newErrors.discountValue = 'Invalid number';
        } else if (discValue < 0) {
          newErrors.discountValue = OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_MIN;
        } else if (discValue > 100) {
          newErrors.discountValue = OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_MAX;
        } else {
          const decimals = discValueStr.split('.')[1];
          if (decimals && decimals.length > 2) {
            newErrors.discountValue =
              OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_DECIMAL;
          }
        }
      }

      if (!startDate) {
        newErrors.startDate = OFFER_ERROR_MESSAGES.DATE_REQUIRED;
      }

      if (!endDate) {
        newErrors.endDate = OFFER_ERROR_MESSAGES.INVALID_END_DATE_FORMAT;
      } else if (startDate && isBefore(endDate, startDate)) {
        newErrors.endDate = OFFER_ERROR_MESSAGES.END_DATE_BEFORE_START;
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
        setIsSubmitting(true);
        try {
          const result = await editOffer({
            offerId,
            offerName: formData.offerName,
            subscriptionPlanIds: selectedPlans,
            discountType: formData.offerType,
            discountValue: formData.discountValue,
            startDate: startDate as Date,
            endDate: endDate as Date,
            status,
          });

          if (result.success) {
            navigate('/Oferty', {
              state: { successMessage: 'Oferta została zaktualizowana.' },
            });
          } else {
            const errorMsg = result.error || OFFER_ERROR_MESSAGES.DEFAULT_ERROR;
            if (
              errorMsg.toLowerCase().includes('already exists') &&
              errorMsg.toLowerCase().includes('name')
            ) {
              setErrors((prev) => ({
                ...prev,
                offerName: OFFER_ERROR_MESSAGES.OFFER_NAME_EXISTS,
              }));
            } else {
              setSnackbarError(errorMsg);
              setIsSnackbarOpen(true);
            }

            if (result.fieldErrors) {
              setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
            }
          }
        } catch (_error) {
          setSnackbarError(OFFER_ERROR_MESSAGES.UNEXPECTED_ERROR);
          setIsSnackbarOpen(true);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [offerId, formData, selectedPlans, startDate, endDate, status, navigate]
  );

  const handleStatusChange = useCallback(async () => {
    const newStatus = status === 'active' ? 'inactive' : 'active';
    setIsStatusSubmitting(true);
    try {
      const result = await editOffer({
        offerId,
        offerName: formData.offerName,
        subscriptionPlanIds: selectedPlans,
        discountType: formData.offerType,
        discountValue: formData.discountValue,
        startDate: startDate as Date,
        endDate: endDate as Date,
        status: newStatus,
      });

      if (result.success) {
        setStatus(newStatus);
      } else {
        setSnackbarError(result.error || 'Błąd podczas zmiany statusu');
        setIsSnackbarOpen(true);
      }
    } catch (_err) {
      setSnackbarError('Wystąpił nieoczekiwany błąd');
      setIsSnackbarOpen(true);
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [offerId, status, formData, selectedPlans, startDate, endDate]);

  const hasChanges = useMemo(() => {
    const original = originalRef.current;
    if (!original) {
      return false;
    }
    if (
      formData.offerName !== original.formData.offerName ||
      formData.offerType !== original.formData.offerType ||
      formData.discountValue !== original.formData.discountValue
    ) {
      return true;
    }
    if (selectedPlans.length !== original.selectedPlans.length) {
      return true;
    }
    if (selectedPlans.some((id, i) => id !== original.selectedPlans[i])) {
      return true;
    }
    const startMs = startDate ? startDate.getTime() : null;
    const endMs = endDate ? endDate.getTime() : null;
    return startMs !== original.startDateMs || endMs !== original.endDateMs;
  }, [formData, selectedPlans, startDate, endDate]);

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate(-1);
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(-1);
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    selectedPlans,
    setSelectedPlans,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    status,
    setStatus,
    isSubmitting,
    isStatusSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleStatusChange,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    availablePlans,
    isLoading,
  };
}
